import React, { useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';

export const EnhancedPerformanceOptimizer: React.FC = () => {
  
  // Optimize critical resource loading with forced reflow prevention
  const optimizeCriticalResources = useCallback(() => {
    // Use requestIdleCallback to prevent forced reflows
    const idleCallback = window.requestIdleCallback || ((cb) => setTimeout(cb, 1));
    
    idleCallback(() => {
      // Preload critical fonts with optimized loading
      const criticalFonts = [
        'https://fonts.googleapis.com/css2?family=Fredoka:wght@300;400;500;600;700&display=swap',
        'https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap',
        'https://fonts.googleapis.com/css2?family=Baloo+2:wght@400;500;600;700&display=swap'
      ];
      
      criticalFonts.forEach(fontUrl => {
        if (!document.querySelector(`link[href="${fontUrl}"]`)) {
          const link = document.createElement('link');
          link.rel = 'preload';
          link.href = fontUrl;
          link.as = 'style';
          link.crossOrigin = 'anonymous';
          link.onload = function(this: HTMLLinkElement) {
            this.onload = null;
            this.rel = 'stylesheet';
          };
          document.head.appendChild(link);
        }
      });

      // Optimize images loading - batch operations to prevent layout thrashing
      const images = document.querySelectorAll('img:not([loading])');
      images.forEach((img, index) => {
        // First 3 images load eagerly (above fold), rest lazy
        img.setAttribute('loading', index < 3 ? 'eager' : 'lazy');
        img.setAttribute('decoding', 'async');
        
        // Add error handling for broken images
        img.addEventListener('error', function() {
          this.style.display = 'none';
          console.warn('Failed to load image:', this.src);
        });
      });

      // Optimize videos
      const videos = document.querySelectorAll('video');
      videos.forEach(video => {
        video.setAttribute('preload', 'metadata');
        video.setAttribute('loading', 'lazy');
      });
    });
  }, []);

  // Performance monitoring and optimization
  const setupPerformanceMonitoring = useCallback(() => {
    // Web Vitals monitoring
    if ('PerformanceObserver' in window) {
      try {
        // Monitor Largest Contentful Paint (LCP)
        const lcpObserver = new PerformanceObserver((list) => {
          list.getEntries().forEach((entry) => {
            if (entry.entryType === 'largest-contentful-paint') {
              const lcp = entry.startTime;
              console.log('LCP:', lcp);
              
              // Track performance issues
              if (lcp > 2500) {
                console.warn('Poor LCP performance detected:', lcp);
              }
            }
          });
        });
        lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });

        // Monitor First Input Delay (FID)
        const fidObserver = new PerformanceObserver((list) => {
          list.getEntries().forEach((entry) => {
            if (entry.entryType === 'first-input') {
              const fid = (entry as any).processingStart - entry.startTime;
              console.log('FID:', fid);
              
              if (fid > 100) {
                console.warn('Poor FID performance detected:', fid);
              }
            }
          });
        });
        fidObserver.observe({ entryTypes: ['first-input'] });

        // Monitor Cumulative Layout Shift (CLS)
        const clsObserver = new PerformanceObserver((list) => {
          let clsValue = 0;
          list.getEntries().forEach((entry) => {
            if (entry.entryType === 'layout-shift' && !(entry as any).hadRecentInput) {
              clsValue += (entry as any).value;
            }
          });
          
          if (clsValue > 0.1) {
            console.warn('Poor CLS performance detected:', clsValue);
          }
        });
        clsObserver.observe({ entryTypes: ['layout-shift'] });
      } catch (error) {
        console.warn('Performance monitoring setup failed:', error);
      }
    }

    // Resource loading optimization - defer to prevent forced reflows
    if ('connection' in navigator) {
      const connection = (navigator as any).connection;
      if (connection && connection.effectiveType) {
        const isSlowConnection = ['slow-2g', '2g', '3g'].includes(connection.effectiveType);
        
        if (isSlowConnection) {
          // Use requestIdleCallback to defer operations
          const idleCallback = window.requestIdleCallback || ((cb) => setTimeout(cb, 1));
          
          idleCallback(() => {
            // Defer non-critical resources on slow connections
            document.documentElement.classList.add('slow-connection');
            
            // Reduce image quality for slow connections
            const images = document.querySelectorAll('img[src*="quality="]');
            images.forEach(img => {
              const src = img.getAttribute('src');
              if (src) {
                img.setAttribute('src', src.replace(/quality=\d+/, 'quality=50'));
              }
            });
          });
        }
      }
    }
  }, []);

  // Resource preloading strategy
  const preloadCriticalResources = useCallback(() => {
    const criticalResources = [
      { href: '/og-image.png', as: 'image', type: 'image/png' },
      { href: '/icons/icon-192.png', as: 'image', type: 'image/png' },
      { href: '/manifest.json', as: 'fetch', type: 'application/json' }
    ];

    criticalResources.forEach(resource => {
      if (!document.querySelector(`link[href="${resource.href}"]`)) {
        const link = document.createElement('link');
        link.rel = 'preload';
        link.href = resource.href;
        link.as = resource.as;
        if (resource.type) link.type = resource.type;
        document.head.appendChild(link);
      }
    });
  }, []);

  // Initialize optimizations
  useEffect(() => {
    // Run critical optimizations immediately
    optimizeCriticalResources();
    preloadCriticalResources();
    
    // Setup monitoring
    const timeoutId = setTimeout(setupPerformanceMonitoring, 100);

    // Cleanup function
    return () => {
      clearTimeout(timeoutId);
    };
  }, [optimizeCriticalResources, preloadCriticalResources, setupPerformanceMonitoring]);

  return (
    <Helmet>
      {/* Critical resource hints */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link rel="preconnect" href="https://www.googletagmanager.com" />
      <link rel="preconnect" href="https://www.google-analytics.com" />
      <link rel="preconnect" href="https://cdnjs.cloudflare.com" />
      
      {/* DNS prefetch for external domains */}
      <link rel="dns-prefetch" href="//fonts.googleapis.com" />
      <link rel="dns-prefetch" href="//fonts.gstatic.com" />
      <link rel="dns-prefetch" href="//www.googletagmanager.com" />
      <link rel="dns-prefetch" href="//www.google-analytics.com" />
      <link rel="dns-prefetch" href="//cdnjs.cloudflare.com" />
      <link rel="dns-prefetch" href="//youtube.com" />
      <link rel="dns-prefetch" href="//www.youtube.com" />
      
      {/* Preload critical assets */}
      <link
        rel="preload"
        href="/og-image.png"
        as="image"
        type="image/png"
      />
      <link
        rel="preload"
        href="/icons/icon-192.png"
        as="image"
        type="image/png"
      />
      
      {/* Performance and user experience optimizations */}
      <meta name="viewport" content="width=device-width, initial-scale=1, viewport-fit=cover" />
      <meta httpEquiv="Accept-CH" content="DPR, Viewport-Width, Width" />
      <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
      
      {/* Prefetch key routes for faster navigation */}
      <link rel="prefetch" href="/math" />
      <link rel="prefetch" href="/science" />
      <link rel="prefetch" href="/games" />
      <link rel="prefetch" href="/stories" />
      
      {/* Service worker registration hint */}
      <link rel="serviceworker" href="/service-worker.js" />
    </Helmet>
  );
};

export default EnhancedPerformanceOptimizer;